import { frontendLogger } from '../../shared/logger';

export interface PlayerScore {
  id: string;
  name: string;
  kills: number;
  deaths: number;
  score: number;
  streak: number;
  isBot: boolean;
}

export interface GameModeConfig {
  name: string;
  description: string;
  scoreLimit: number;
  timeLimit: number; // in seconds (0 = no time limit)
  respawnTime: number; // in seconds
  botCount: number;
  botDifficulty: 'easy' | 'medium' | 'hard';
  timeLimitOptions?: number[]; // Available time options in seconds
}

export abstract class GameMode {
  protected scores: Map<string, PlayerScore> = new Map();
  protected startTime: number = 0;
  protected endTime: number = 0;
  protected isActive: boolean = false;
  protected winner: string | null = null;
  
  constructor(protected config: GameModeConfig) {}
  
  abstract onPlayerKill(killerId: string, victimId: string): void;
  abstract onPlayerJoin(playerId: string, name: string, isBot?: boolean): void;
  abstract onPlayerLeave(playerId: string): void;
  abstract checkWinCondition(): boolean;
  abstract getLeaderboard(): PlayerScore[];
  
  start() {
    this.startTime = Date.now();
    this.isActive = true;
    this.scores.clear();
    this.winner = null;
    frontendLogger.info(`Game mode started: ${this.config.name}`);
  }
  
  end() {
    this.endTime = Date.now();
    this.isActive = false;
    const winner = this.getLeader();
    if (winner) {
      this.winner = winner.id;
      frontendLogger.info(`Game ended. Winner: ${winner.name} with ${winner.score} points`);
    }
  }
  
  getElapsedTime(): number {
    if (!this.isActive) return 0;
    return Math.floor((Date.now() - this.startTime) / 1000);
  }
  
  getRemainingTime(): number {
    if (!this.isActive || this.config.timeLimit === 0) return -1;
    const elapsed = this.getElapsedTime();
    return Math.max(0, this.config.timeLimit - elapsed);
  }
  
  getLeader(): PlayerScore | null {
    let leader: PlayerScore | null = null;
    let maxScore = -1;
    
    for (const score of this.scores.values()) {
      if (score.score > maxScore) {
        maxScore = score.score;
        leader = score;
      }
    }
    
    return leader;
  }
  
  getPlayerScore(playerId: string): PlayerScore | null {
    return this.scores.get(playerId) || null;
  }
  
  isGameActive(): boolean {
    return this.isActive;
  }
  
  getWinner(): string | null {
    return this.winner;
  }
}

export class FreeForAllMode extends GameMode {
  private killPoints = 1;  // Simple 1 point per kill
  // private deathPenalty = 0; // No death penalty (not used)
  // private streakBonus = 0;  // No streak bonuses (not used)
  
  constructor(config?: Partial<GameModeConfig>) {
    super({
      name: 'Free For All',
      description: 'Every player for themselves. First to 20 kills wins!',
      scoreLimit: 20, // First to 20 kills wins
      timeLimit: 300, // 5 minutes default (adjustable)
      respawnTime: 3,
      botCount: 4,
      botDifficulty: 'medium',
      ...config
    });
  }
  
  onPlayerJoin(playerId: string, name: string, isBot: boolean = false) {
    if (!this.scores.has(playerId)) {
      this.scores.set(playerId, {
        id: playerId,
        name,
        kills: 0,
        deaths: 0,
        score: 0,
        streak: 0,
        isBot
      });
      frontendLogger.info(`Player joined FFA: ${name} (${isBot ? 'Bot' : 'Human'})`);
    }
  }
  
  onPlayerLeave(playerId: string) {
    this.scores.delete(playerId);
    frontendLogger.info(`Player left FFA: ${playerId}`);
  }
  
  onPlayerKill(killerId: string, victimId: string) {
    const killer = this.scores.get(killerId);
    const victim = this.scores.get(victimId);
    
    if (!killer || !victim) return;
    
    // Update killer stats - simple 1 point per kill
    killer.kills++;
    killer.score += this.killPoints;
    
    // Update victim stats - just track deaths, no penalty
    victim.deaths++;
    
    // Check win condition
    if (this.checkWinCondition()) {
      this.end();
    }
    
    // Log the kill
    frontendLogger.info(`${killer.name} killed ${victim.name} [Score: ${killer.score}/${this.config.scoreLimit}]`);
  }
  
  checkWinCondition(): boolean {
    // Check score limit
    for (const score of this.scores.values()) {
      if (score.score >= this.config.scoreLimit) {
        return true;
      }
    }
    
    // Check time limit
    if (this.config.timeLimit > 0 && this.getElapsedTime() >= this.config.timeLimit) {
      return true;
    }
    
    return false;
  }
  
  getLeaderboard(): PlayerScore[] {
    return Array.from(this.scores.values())
      .sort((a, b) => {
        // Sort by score first
        if (b.score !== a.score) return b.score - a.score;
        // Then by K/D ratio
        const kdA = a.deaths === 0 ? a.kills : a.kills / a.deaths;
        const kdB = b.deaths === 0 ? b.kills : b.kills / b.deaths;
        return kdB - kdA;
      });
  }
  
  getKDRatio(playerId: string): number {
    const score = this.scores.get(playerId);
    if (!score) return 0;
    return score.deaths === 0 ? score.kills : Number((score.kills / score.deaths).toFixed(2));
  }
}

// Team Deathmatch mode for future expansion
export class TeamDeathmatchMode extends GameMode {
  private teams: Map<string, Set<string>> = new Map();
  private teamScores: Map<string, number> = new Map();
  
  constructor(config?: Partial<GameModeConfig>) {
    super({
      name: 'Team Deathmatch',
      description: 'Two teams battle for supremacy!',
      scoreLimit: 5000,
      timeLimit: 900, // 15 minutes
      respawnTime: 5,
      botCount: 6,
      botDifficulty: 'medium',
      ...config
    });
    
    // Initialize teams
    this.teams.set('red', new Set());
    this.teams.set('blue', new Set());
    this.teamScores.set('red', 0);
    this.teamScores.set('blue', 0);
  }
  
  onPlayerJoin(playerId: string, name: string, isBot: boolean = false) {
    // Auto-balance teams
    const redCount = this.teams.get('red')?.size || 0;
    const blueCount = this.teams.get('blue')?.size || 0;
    const team = redCount <= blueCount ? 'red' : 'blue';
    
    this.teams.get(team)?.add(playerId);
    this.scores.set(playerId, {
      id: playerId,
      name,
      kills: 0,
      deaths: 0,
      score: 0,
      streak: 0,
      isBot
    });
    
    frontendLogger.info(`${name} joined team ${team}`);
  }
  
  onPlayerLeave(playerId: string) {
    // Remove from teams
    this.teams.forEach(team => team.delete(playerId));
    this.scores.delete(playerId);
  }
  
  onPlayerKill(killerId: string, victimId: string) {
    const killer = this.scores.get(killerId);
    const victim = this.scores.get(victimId);
    
    if (!killer || !victim) return;
    
    // Check if friendly fire (same team)
    let killerTeam: string | null = null;
    let victimTeam: string | null = null;
    
    this.teams.forEach((members, team) => {
      if (members.has(killerId)) killerTeam = team;
      if (members.has(victimId)) victimTeam = team;
    });
    
    if (killerTeam === victimTeam) {
      // Friendly fire - penalize
      killer.score -= 50;
      frontendLogger.warn(`Friendly fire: ${killer.name} killed teammate ${victim.name}`);
      return;
    }
    
    // Update stats
    killer.kills++;
    killer.score += 100;
    victim.deaths++;
    
    // Update team score
    if (killerTeam) {
      const currentScore = this.teamScores.get(killerTeam) || 0;
      this.teamScores.set(killerTeam, currentScore + 100);
    }
    
    if (this.checkWinCondition()) {
      this.end();
    }
  }
  
  checkWinCondition(): boolean {
    for (const score of this.teamScores.values()) {
      if (score >= this.config.scoreLimit) {
        return true;
      }
    }
    
    if (this.config.timeLimit > 0 && this.getElapsedTime() >= this.config.timeLimit) {
      return true;
    }
    
    return false;
  }
  
  getLeaderboard(): PlayerScore[] {
    return Array.from(this.scores.values()).sort((a, b) => b.score - a.score);
  }
  
  getTeamScores(): Map<string, number> {
    return this.teamScores;
  }
}
